import { useMemo } from 'react';
import Lottie from 'lottie-react';
import { Sparkle } from '@phosphor-icons/react';
import truckAnimation from '../../assets/miles-truck-animation.json';

type AvatarSize = 'xs' | 'sm' | 'smmd' | 'md' | 'lg' | 'hero';

interface MilesAvatarProps {
  /**
   * Size variant for different contexts:
   * - xs: 24px - Inline message avatar
   * - sm: 32px - Message list avatar
   * - md: 64px - Loading states
   * - lg: 120px - Empty states
   * - hero: 200px - Welcome screen hero
   */
  size?: AvatarSize;
  /**
   * Whether to animate the Lottie. Set to false for static display.
   * @default true
   */
  animate?: boolean;
  /**
   * Whether to loop the animation.
   * @default true
   */
  loop?: boolean;
  /**
   * Animation speed multiplier.
   * @default 1
   */
  speed?: number;
  /**
   * Optional CSS class for additional styling.
   */
  className?: string;
  /**
   * Whether to use a simplified static version (Sparkle icon) for very small sizes.
   * Helps with performance when many avatars are rendered.
   * @default false
   */
  useStaticFallback?: boolean;
}

const SIZE_CONFIG: Record<
  AvatarSize,
  { dimension: number; containerClass: string }
> = {
  xs: { dimension: 24, containerClass: 'h-6 w-6' },
  sm: { dimension: 32, containerClass: 'h-8 w-8' },
  smmd: { dimension: 60, containerClass: 'h-14 w-14' },
  md: { dimension: 64, containerClass: 'h-16 w-16' },
  lg: { dimension: 120, containerClass: 'h-[120px] w-[120px]' },
  hero: { dimension: 200, containerClass: 'h-[200px] w-[200px]' },
};

/**
 * MilesAvatar - The animated MILES mascot component.
 *
 * Uses a Lottie animation of a TACH truck with dollar signs to represent
 * MILES, the AI financial assistant. Scales from tiny inline avatars to
 * full hero-sized welcome animations.
 *
 * Design inspiration: Robinhood's contextual use of their feather mascot
 * - Large for hero moments (welcome, celebrations)
 * - Compact for inline loading states
 * - Static fallback for performance-critical areas
 */
function MilesAvatar({
  size = 'md',
  animate = true,
  loop = true,
  speed = 1,
  className = '',
  useStaticFallback = false,
}: MilesAvatarProps) {
  const config = SIZE_CONFIG[size];

  // For very small sizes or when static fallback requested, use icon instead
  const shouldUseStatic = useStaticFallback || size === 'xs';

  // Memoize Lottie options to prevent unnecessary re-renders
  const lottieStyle = useMemo(
    () => ({
      width: config.dimension,
      height: config.dimension,
    }),
    [config.dimension]
  );

  if (shouldUseStatic) {
    return (
      <div
        className={`flex items-center justify-center rounded-full bg-primary/10 ${config.containerClass} ${className}`}
      >
        <Sparkle
          size={config.dimension * 0.5}
          weight="fill"
          className="text-primary"
        />
      </div>
    );
  }

  return (
    <div
      className={`relative flex items-center justify-center ${config.containerClass} ${className}`}
    >
      {/* Subtle background glow for larger sizes */}
      {(size === 'lg' || size === 'hero') && (
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-secondary/20 to-accent/10 blur-xl" />
      )}

      {/* Lottie Animation */}
      <Lottie
        animationData={truckAnimation}
        loop={loop}
        autoplay={animate}
        style={lottieStyle}
        className="relative z-10"
      />
    </div>
  );
}

export default MilesAvatar;
